import { useNavigate } from "react-router-dom";
import CommentResponseResource from "common/resources/Project/Config/Forum/Comment/CommentResponseResource";
import classes from "./classes.module.scss";
import CommentAuthorInfo from "./CommentAuthorInfo";
import CommentLikeButton from "./CommentLikeButon";
import CommentDeleteButton from "./CommentDeleteButton";
import Typography from "components/elements/Typography";
import CommentReplyForm from "./CommentReplyForm";
import CommentResponseButton from "./CommentResponseButton";
import { useProjectId } from "hooks/useProjectId";
import { usePostId } from "hooks/usePostId";
import { useCallback, useEffect, useMemo, useState } from "react";
import { container } from "tsyringe";
import CommentService from "services/projects/CommentService";
import { upsert } from "common/utils/array";
import CommentReplyButton from "./CommentReplyButton";
import ModuleConfig from "configs/ModuleConfig";

type IProps = {
	comment: CommentResponseResource;
	depth: number;
	onDeleted: (comment: Pick<CommentResponseResource, "id">) => void;
};

const modules = container.resolve(ModuleConfig).get().modules;
const commentService = container.resolve(CommentService);
const maxDepth = 4;

export default function CommentCard(props: IProps) {
	const maxDepthReached = useMemo(() => props.depth === maxDepth, [props.depth]);
	const { replies, removeReply, upsertReply } = useReplies(props.comment, maxDepthReached);
	const { areRepliesCollapsed, unfoldReplies, toggleReplyCollapse } = useCollapsedComments();
	const { navigateToComment } = useNavigateToComment(props.comment.id);
	const { closeReplyForm, isReplying, openReplyForm } = useReplyButton();

	const onUnfoldReplies = useCallback(() => {
		if (maxDepthReached) return navigateToComment();
		unfoldReplies();
	}, [maxDepthReached, navigateToComment, unfoldReplies]);

	const onToggleReplyCollapse = useCallback(() => {
		if (maxDepthReached) return navigateToComment();
		toggleReplyCollapse();
	}, [maxDepthReached, navigateToComment, toggleReplyCollapse]);

	const onCreated = useCallback(
		(comment: CommentResponseResource) => {
			upsertReply(comment);
			closeReplyForm();
			onUnfoldReplies();
		},
		[closeReplyForm, onUnfoldReplies, upsertReply],
	);

	return (
		<div className={classes["root"]}>
			<div className={classes["comment-card"]}>
				<div className={classes["header"]}>
					<CommentAuthorInfo comment={props.comment} />
					<div className={classes["action-button"]}>
						<CommentLikeButton comment={props.comment} />
						<CommentDeleteButton comment={props.comment} onDeleted={props.onDeleted} />
					</div>
				</div>

				<Typography type="p" weight="regular" size="medium">
					{props.comment.text}
				</Typography>

				<div className={classes["action-button"]}>
					<CommentReplyButton onClick={openReplyForm} />
					<CommentResponseButton
						comment={props.comment}
						onToggleCollapse={onToggleReplyCollapse}
						areRepliesCollapsed={areRepliesCollapsed}
						maxDepthReached={maxDepthReached}
						repliesCount={replies.length}
					/>
				</div>

				{isReplying && <CommentReplyForm comment={props.comment} onCreated={onCreated} onCancel={closeReplyForm} />}
			</div>

			{!areRepliesCollapsed && replies && (
				<div className={classes["replies"]}>
					{replies.map((reply) => (
						<CommentCard key={reply.id} comment={reply} depth={props.depth + 1} onDeleted={removeReply} />
					))}
				</div>
			)}
		</div>
	);
}

const useReplyButton = () => {
	const [isReplying, setIsReplying] = useState(false);

	const openReplyForm = useCallback(() => setIsReplying(true), []);
	const closeReplyForm = useCallback(() => setIsReplying(false), []);

	return { isReplying, openReplyForm, closeReplyForm };
};

const useCollapsedComments = () => {
	const [areRepliesCollapsed, setIsRepliesCollapsed] = useState(true);

	const unfoldReplies = useCallback(() => {
		setIsRepliesCollapsed(false);
	}, []);

	const toggleReplyCollapse = useCallback(() => {
		setIsRepliesCollapsed((isCollapsed) => !isCollapsed);
	}, []);

	return { areRepliesCollapsed, unfoldReplies, toggleReplyCollapse };
};

const useReplies = (comment: Pick<CommentResponseResource, "id" | "replies">, maxDepthReached: boolean) => {
	const { projectId } = useProjectId();
	const { postId } = usePostId();
	const [replies, setReplies] = useState<CommentResponseResource[]>(comment.replies ?? []);

	const fetchReplies = useCallback(async () => {
		return commentService.getById(projectId, postId, comment.id).then(({ replies }) => setReplies(replies ?? []));
	}, [projectId, postId, comment.id]);

	const upsertReply = useCallback((reply: CommentResponseResource) => {
		setReplies((replies) => upsert(replies, reply, (r) => r.id === reply.id, { prepend: true }));
		return reply;
	}, []);

	const removeReply = useCallback((deletedReply: Pick<CommentResponseResource, "id">) => {
		setReplies((replies) => replies.filter((r) => r.id !== deletedReply.id));
		return deletedReply;
	}, []);

	useEffect(() => {
		if (!maxDepthReached && comment.replies === undefined) {
			fetchReplies();
		}
	}, [comment.replies, fetchReplies, maxDepthReached]);

	return {
		replies,
		upsertReply,
		removeReply,
	};
};

const useNavigateToComment = (commentId: string) => {
	const navigate = useNavigate();
	const { projectId } = useProjectId();
	const { postId } = usePostId();

	const navigateToComment = useCallback(() => {
		navigate(modules.pages.ForumPostComment.props.path.replace(":projectId", projectId).replace(":postId", postId).replace(":commentId", commentId));
	}, [navigate, projectId, postId, commentId]);

	return {
		navigateToComment,
	};
};
